import { parseExtraUnitInfo, UNIT_NOTES_EXTRA_LABEL } from './dailyServiceUnitPrompts';

export { UNIT_NOTES_EXTRA_LABEL };

const NOTE_STAMP_LINE = /^[A-Z][a-z]{2} \d{1,2}, \d{4}, \d{1,2}:\d{2} [AP]M — .+$/;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local date, time, and name, ready to sit above a new note. */
export function formatNoteStamp(actorName: string, at: Date = new Date()): string {
    const month = MONTHS[at.getMonth()];
    const day = at.getDate();
    const year = at.getFullYear();
    const minutes = String(at.getMinutes()).padStart(2, '0');
    const ampm = at.getHours() >= 12 ? 'PM' : 'AM';
    const hours = at.getHours() % 12 || 12;
    const who = actorName.trim() || 'Unknown';
    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm} — ${who}`;
}

export function appendStampedNote(
    existing: string | null | undefined,
    addition: string,
    actorName: string,
    at: Date = new Date(),
): string {
    const text = addition.trim();
    const prior = (existing || '').trim();
    if (!text) return prior;
    const block = `${formatNoteStamp(actorName, at)}\n${text}`;
    return prior ? `${prior}\n\n${block}` : block;
}

/** Put a stamp at the end of a notes box when the person starts a new entry. */
export function withInsertedNoteStamp(value: string, actorName: string, at: Date = new Date()): string {
    const trimmed = value.replace(/\s+$/, '');
    const lines = trimmed.split('\n');
    const last = (lines[lines.length - 1] || '').trim();
    if (last && NOTE_STAMP_LINE.test(last)) {
        return trimmed.endsWith('\n') ? trimmed : `${trimmed}\n`;
    }
    const stamp = formatNoteStamp(actorName, at);
    return trimmed ? `${trimmed}\n\n${stamp}\n` : `${stamp}\n`;
}

/** Drop a stamp that was inserted and then left blank. */
export function stripDanglingNoteStamp(value: string): string {
    const lines = value.replace(/\s+$/, '').split('\n');
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
        lines.pop();
    }
    if (lines.length > 0 && NOTE_STAMP_LINE.test(lines[lines.length - 1].trim())) {
        lines.pop();
        while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
            lines.pop();
        }
    }
    return lines.join('\n');
}

export function getUnitNotes(extraUnitInfo: string | null | undefined): string {
    const match = parseExtraUnitInfo(extraUnitInfo).find((entry) => entry.label === UNIT_NOTES_EXTRA_LABEL);
    return match?.value?.trim() || '';
}

export function upsertUnitNotes(
    extraUnitInfo: string | null | undefined,
    notes: string,
): string | null {
    const extras = parseExtraUnitInfo(extraUnitInfo).filter((entry) => entry.label !== UNIT_NOTES_EXTRA_LABEL);
    const trimmed = notes.trim();
    if (trimmed) {
        extras.push({ label: UNIT_NOTES_EXTRA_LABEL, value: trimmed });
    }
    return extras.length ? JSON.stringify(extras) : null;
}
